import { useRef, useMemo } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Drop-in replacement for a plain <ReactQuill>, with a fuller toolbar
// (headings, formatting, blockquote, code block, lists, link, image) and a
// custom image handler: instead of embedding the picked image as a giant
// base64 blob inline (ReactQuill's default), it uploads to Cloudinary via
// the shared upload endpoint and inserts the hosted URL instead — keeps
// documents small and images fast to load.
const RichTextEditor = ({ value, onChange }) => {
    const quillRef = useRef(null);

    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const { data } = await axios.post('/api/v1/admin/upload-image', { image: reader.result });
                    const editor = quillRef.current.getEditor();
                    const range = editor.getSelection(true);
                    editor.insertEmbed(range.index, 'image', data.url);
                    editor.setSelection(range.index + 1);
                } catch (error) {
                    console.error('Image upload failed', error);
                }
            };
            reader.readAsDataURL(file);
        };
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image'],
                ['clean'],
            ],
            handlers: {
                image: imageHandler,
            },
        },
    }), []);

    return (
        <div>
            <ReactQuill ref={quillRef} theme="snow" value={value} onChange={onChange} modules={modules} />
            <button
                type="button"
                onClick={() => onChange('')}
                className="text-xs text-gray-500 hover:text-red-500 hover:underline mt-1"
            >
                Clear content
            </button>
        </div>
    );
};

export default RichTextEditor;
